// The VPN's profile and DNS (D80). The profile is a `vpn_profile` secret uploaded on the
// Secrets page; DNS servers and domains are for names only the VPN resolves.
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import { Button, Input } from "rizzui";

import { useT } from "../i18n";
import { credentialsQuery } from "../secrets/api";
import { listItems } from "../secrets/templates";
import { SelectField } from "../ui/SideDrawer";
import type { EnvironmentVpn, VpnSettingsBody } from "./api";

interface Props {
  orgId: string;
  vpn: EnvironmentVpn;
  saving: boolean;
  onSave: (body: VpnSettingsBody) => void;
}

export function VpnSettingsForm({ orgId, vpn, saving, onSave }: Props) {
  const t = useT().vpn.settings;
  const secrets = useQuery(credentialsQuery(orgId));
  const profiles = (secrets.data ?? []).filter((c) => c.kind === "vpn_profile");
  const [profile, setProfile] = useState(vpn.settings?.profile_credential_id ?? "");
  const [servers, setServers] = useState((vpn.settings?.dns_servers ?? []).join(", "));
  const [domains, setDomains] = useState((vpn.settings?.dns_domains ?? []).join(", "));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ profile_credential_id: profile || null, dns_servers: listItems(servers), dns_domains: listItems(domains) });
  };
  return (
    <form className="space-y-4" onSubmit={submit} aria-label={t.title}>
      <p className="m-0 text-[13px] text-gray-600">{t.hint}</p>
      <SelectField
        label={t.profile}
        value={profile}
        options={[{ value: "", label: t.none }, ...profiles.map((c) => ({ value: c.id, label: c.name }))]}
        onChange={setProfile}
      />
      {secrets.isSuccess && profiles.length === 0 ? (
        <p className="m-0 text-[13px] text-gray-600">
          {t.noProfiles}{" "}
          <Link to="/secrets?add=vpn_profile" className="font-medium text-primary hover:underline">
            {t.addProfile}
          </Link>
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label={t.dnsServers} value={servers} placeholder="10.0.10.156" helperText={t.dnsServersHint} onChange={(e) => setServers(e.target.value)} />
        <Input label={t.dnsDomains} value={domains} placeholder="aws" helperText={t.dnsDomainsHint} onChange={(e) => setDomains(e.target.value)} />
      </div>
      <Button type="submit" isLoading={saving}>
        {t.save}
      </Button>
    </form>
  );
}
